import ApiClient from "../helpers/ApiClient";
import useSWR from "swr";

const fetcher = (url) => ApiClient.get(url).then((res) => res.data);

export function useInventoryItems({status} = {}) {
  const APIURL = `/inventory?status=${status}`;
  const { data, error, isLoading } = useSWR(APIURL, fetcher);
  return {
    data,
    error,
    isLoading,
    APIURL,
  };
}

export function useInventoryCategories() {
  const APIURL = `/inventory/categories`;
  const { data, error, isLoading } = useSWR(APIURL, fetcher);
  return {
    categories: data?.categories || [],
    error,
    isLoading,
    APIURL,
  };
}

export async function addInventoryCategory({ name }) {
  try {
    const response = await ApiClient.post(`/inventory/categories`, { name });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function deleteInventoryCategory({ id }) {
  try {
    const response = await ApiClient.delete(`/inventory/categories/${id}`);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function addInventoryItem({title, quantity, unit, min_quantity_threshold, category = "Category"}) {
  try {
    const response = await ApiClient.post(`/inventory/add-item`, {
      title, quantity, unit, min_quantity_threshold, category
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function updateInventoryItem({id, title, unit, min_quantity_threshold, category = "Category"}) {
  try {
    const response = await ApiClient.put(`/inventory/${id}`, {
      title, unit, min_quantity_threshold, category
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function addInventoryItemStockMovement({id, movementType, quantity, note }) {
  try {
    const response = await ApiClient.patch(`/inventory/${id}/add-stock-movement`, {
      movementType, quantity, note
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function deleteInventoryItem({id}) {
  try {
    const response = await ApiClient.delete(`/inventory/${id}`);
    return response;
  } catch (error) {
    throw error;
  }
}

export function useInventoryLogs({ type, from = null, to = null, movementType = null } = {}) {
  const APIURL = `/inventory/logs?type=${type}&from=${from}&to=${to}&movementType=${movementType}`;
  const { data, error, isLoading } = useSWR(APIURL, fetcher);
  return {
    data,
    error,
    isLoading,
    APIURL,
  };
}

export function useInventoryDashboard({ type, from = null, to = null} = {}) {
  const APIURL = `/inventory/dashboard?type=${type}&from=${from}&to=${to}`;
  const { data, error, isLoading } = useSWR(APIURL, fetcher);
  return {
    data,
    error,
    isLoading,
    APIURL,
  };
}

// Suppliers
export function useSuppliers() {
  const APIURL = `/suppliers`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return {
    data,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export async function addSupplier({ name, contact_person, phone, email, address, openingBalance, panNo, isVatRegistered }) {
  try {
    const response = await ApiClient.post(`/suppliers`, { name, contact_person, phone, email, address, openingBalance, panNo, isVatRegistered });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function updateSupplier(id, { name, contact_person, phone, email, address, openingBalance, panNo, isVatRegistered }) {
  try {
    const response = await ApiClient.put(`/suppliers/${id}`, { name, contact_person, phone, email, address, openingBalance, panNo, isVatRegistered });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function deleteSupplier(id) {
  try {
    const response = await ApiClient.delete(`/suppliers/${id}`);
    return response;
  } catch (error) {
    throw error;
  }
}

// Purchases
export function usePurchases({ from = null, to = null } = {}) {
  const query = from && to ? `?from=${from}&to=${to}` : "";
  const APIURL = `/purchase-invoices${query}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher, { keepPreviousData: true });
  return {
    data,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export async function createPurchase(data) {
  try {
    const response = await ApiClient.post(`/purchase-invoices`, data);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getPurchaseInvoiceById(id) {
  try {
    const response = await ApiClient.get(`/purchase-invoices/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function paySupplierDue({ purchase_id, amount, payment_date, payment_method, notes, multi_payments }) {
  try {
    const response = await ApiClient.post(`/purchase-invoices/payments`, { purchase_id, amount, payment_date, payment_method, notes, multi_payments });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function bulkPaySupplierDue({ supplier_id, amount, payment_date, payment_method, notes, multi_payments }) {
  try {
    const response = await ApiClient.post(`/purchase-invoices/bulk-pay`, { supplier_id, amount, payment_date, payment_method, notes, multi_payments });
    return response;
  } catch (error) {
    throw error;
  }
}

export function useSupplierDues(search = "") {
  const APIURL = `/purchase-invoices/supplier-dues?search=${encodeURIComponent(search)}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher, { keepPreviousData: true });
  return {
    data,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export function useSupplierStatement(supplierId, from = "", to = "") {
  const query = [];
  if (from) query.push(`from=${from}`);
  if (to) query.push(`to=${to}`);
  const qStr = query.length > 0 ? `?${query.join("&")}` : "";
  const APIURL = supplierId ? `/purchase-invoices/supplier-statement/${supplierId}${qStr}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return {
    data,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export async function uploadBulkInventoryItems(items) {
  try {
    const response = await ApiClient.post("/inventory/bulk", {
      items,
    });
    return response;
  } catch (error) {
    throw error;
  }
}


export async function addBulkStockToAllItems({ quantity, note }) {
  try {
    const response = await ApiClient.post("/inventory/bulk-add-stock-all", { quantity, note });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function setBulkMinStockToAllItems({ min_quantity }) {
  try {
    const response = await ApiClient.post("/inventory/bulk-set-min-stock-all", { min_quantity });
    return response;
  } catch (error) {
    throw error;
  }
}
